import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Drawer from "@mui/material/Drawer";
import InputField from "components/Input/InputField";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { normalizeTermSchedule } from "../Admissions/AddAdmissions/applicationFlow/TermScheduleTable";
import { formatDateForInput, getLockedTermIdsFromInstallments } from "./accountUtils";

export default function EyeModal({
  open,
  setOpen,
  values,
  setValues,
  handleSubmit,
  admissionDetailId,
  readOnly = false,
  lockedInstallments = [],
}) {
  const [{ uniData, courseList }, { refetchUniData, refetchCourseList }] = useKothar();
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  const lockedTermIds = useMemo(
    () => getLockedTermIdsFromInstallments(lockedInstallments),
    [lockedInstallments]
  );

  const termSchedule = useMemo(
    () => normalizeTermSchedule(values?.termSchedule),
    [values?.termSchedule]
  );

  useEffect(() => {
    if (open?.state && (!uniData || uniData.length === 0)) refetchUniData();
    if (open?.state && (!courseList || courseList.length === 0)) refetchCourseList();
  }, [courseList, open?.state, refetchCourseList, refetchUniData, uniData]);

  useEffect(() => {
    const id = Number(values?.universityId);
    if (Number.isFinite(id) && id > 0) loadAgents(id);
    else setAgents([]);
  }, [values?.universityId]);

  const loadAgents = async (uniId) => {
    setAgentsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/universities/${uniId}/agents`);
      setAgents(res?.data?.data || []);
    } catch {
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  };

  const toggleDrawer = () => setOpen({ state: false, uuid: null });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleUniversityChange = (_, selected) => {
    setValues((prev) => ({
      ...prev,
      university: selected?.name ?? "",
      universityId: selected?.id ?? null,
      agentName: "",
    }));
  };

  const handleAgentChange = (_, selected) => {
    setValues((prev) => ({
      ...prev,
      agentName: selected?.name ?? selected ?? "",
    }));
  };

  const isEmptyValue = (value) =>
    value === undefined || value === null || value === "" || value === 0;

  const selectedUniversity =
    uniData?.find((u) => Number(u.id) === Number(values?.universityId)) ??
    (values?.university ? { id: values?.universityId, name: values?.university } : null);
  const selectedCourse =
    courseList?.find((course) => Number(course.courseId) === Number(values?.courseId)) ||
    courseList?.find((course) => course.courseName === values?.course) ||
    (values?.course ? { courseId: values?.courseId, courseName: values?.course } : null);

  useEffect(() => {
    if (!open?.state || !values?.courseId || !courseList?.length) return;
    const course = courseList.find((item) => Number(item?.courseId) === Number(values.courseId));
    const offering =
      course?.offerings?.find(
        (item) => Number(item?.universityId) === Number(values?.universityId)
      ) || {};
    const university = uniData?.find((item) => Number(item?.id) === Number(values?.universityId));
    const onshoreFee = offering?.onshoreFees?.find((item) => item?.state === values?.state) || {};

    setValues((prev) => ({
      ...prev,
      course: prev.course || course?.courseName || "",
      university: prev.university || university?.name || offering?.universityName || "",
      totalFees: isEmptyValue(prev.totalFees)
        ? onshoreFee?.totalFees || offering?.totalFees || prev.totalFees
        : prev.totalFees,
      perSemFee: isEmptyValue(prev.perSemFee)
        ? onshoreFee?.feePerSem || offering?.feePerSem || prev.perSemFee
        : prev.perSemFee,
      materialFee: isEmptyValue(prev.materialFee)
        ? offering?.materialFee || offering?.materialFees || prev.materialFee
        : prev.materialFee,
      enrolmentFee: isEmptyValue(prev.enrolmentFee)
        ? offering?.enrolmentFee || offering?.enrollmentFee || prev.enrolmentFee
        : prev.enrolmentFee,
      semester: isEmptyValue(prev.semester)
        ? prev.termSchedule?.length || offering?.noOfSem || prev.semester
        : prev.semester,
    }));
  }, [
    courseList,
    open?.state,
    setValues,
    uniData,
    values?.courseId,
    values?.state,
    values?.universityId,
  ]);

  const handleCourseChange = (_, selected) => {
    setValues((prev) => ({
      ...prev,
      course: selected?.courseName ?? "",
      courseId: selected?.courseId ?? null,
    }));
  };

  // Resolve fee/discount for AUTO-mode terms using agreedFeePerUnit
  const resolveTermFee = (term) => {
    if (term.termFeeManual) return term.termFee ?? "";
    const feePerUnit = Number(values?.agreedFeePerUnit) || 0;
    const units = Number(term.units) || 0;
    if (feePerUnit > 0 && units > 0) return String(feePerUnit * units);
    return term.termFee ?? "";
  };

  const resolveTermDiscount = (term) => {
    if (term.discountManual) return term.discount ?? "";
    const discPerUnit = Number(values?.agreedDiscountPerUnit) || 0;
    const units = Number(term.units) || 0;
    if (discPerUnit > 0 && units > 0) return String(discPerUnit * units);
    return term.discount ?? "";
  };

  const computeAgreed = (term) => {
    const fee = Number(resolveTermFee(term)) || 0;
    const mat = Number(term.materialFee) || 0;
    const disc = Number(resolveTermDiscount(term)) || 0;
    return fee + mat - disc;
  };

  // Fields from Step 3 (offer letter received fee details)
  const topFields = [
    { label: "Student ID", value: "studentId", type: "text" },
    { label: "Start Date", value: "startDate", type: "date" },
    { label: "End Date", value: "endDate", type: "date" },
    { label: "Number of Terms", value: "semester", type: "number" },
    { label: "Term Fee", value: "agreedFees", type: "number" },
    { label: "Discount/Term", value: "discount", type: "number" },
  ];

  const agreedFeePerTerm = Number(values?.agreedFees || 0) - Number(values?.discount || 0);
  const agreedFeePerUnit =
    Number(values?.agreedFeePerUnit || 0) - Number(values?.agreedDiscountPerUnit || 0);

  return (
    <Drawer
      anchor="right"
      open={open?.state}
      PaperProps={{ style: { width: "min(760px, 100vw)" } }}
    >
      <div className="flex justify-between p-4 border-b">
        <h1 className="text-xl font-semibold">Admission Details</h1>
        <Close onClick={toggleDrawer} className="cursor-pointer" />
      </div>

      <div className="mt-4 p-4 overflow-y-auto flex-1">
        {/* University */}
        <div className="flex justify-between items-center mb-3">
          <span className="w-44 text-sm font-medium">University / College</span>
          <Autocomplete
            size="small"
            options={uniData ?? []}
            getOptionLabel={(o) => o?.name ?? ""}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            value={selectedUniversity}
            onChange={handleUniversityChange}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select University" size="small" />
            )}
            sx={{ flex: 1 }}
            ListboxProps={{ style: { maxHeight: 200 } }}
            disabled
          />
        </div>

        {/* Agent — dropdown with agents for this university, not disabled */}
        <div className="flex justify-between items-center mb-3">
          <span className="w-44 text-sm font-medium">Agent Name</span>
          {agentsLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Autocomplete
              size="small"
              freeSolo
              options={agents}
              getOptionLabel={(o) => (typeof o === "string" ? o : o?.name ?? "")}
              value={values?.agentName ?? ""}
              onChange={handleAgentChange}
              onInputChange={(_, val) => setValues((prev) => ({ ...prev, agentName: val }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={agents.length ? "Select or type agent" : "Type agent name"}
                  size="small"
                />
              )}
              sx={{ flex: 1 }}
              ListboxProps={{ style: { maxHeight: 200 } }}
              disabled
            />
          )}
        </div>

        {/* Course */}
        <div className="flex justify-between items-center mb-3">
          <span className="w-44 text-sm font-medium">Course Name</span>
          <Autocomplete
            size="small"
            options={courseList ?? []}
            getOptionLabel={(o) => o?.courseName ?? ""}
            isOptionEqualToValue={(o, v) => o?.courseId === v?.courseId}
            value={selectedCourse}
            onChange={handleCourseChange}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select Course" size="small" />
            )}
            sx={{ flex: 1 }}
            ListboxProps={{ style: { maxHeight: 200 } }}
            disabled
          />
        </div>

        {/* Step 3 fields: Student ID, dates, then fee details */}
        {topFields.map((field) => (
          <div className="flex justify-between items-center mb-3" key={field.value}>
            <span className="w-44 text-sm font-medium">{field.label}</span>
            <InputField
              type={field.type}
              name={field.value}
              placeholder={field.label}
              size="small"
              onChange={handleInputChange}
              value={
                field.type === "date"
                  ? formatDateForInput(values?.[field.value])
                  : values?.[field.value] ?? ""
              }
              className="flex-1"
              disabled
            />
          </div>
        ))}

        {/* Agreed Fee/Term — computed, grouped with term fees */}
        <div className="flex justify-between items-center mb-3">
          <span className="w-44 text-sm font-medium">Agreed Fee/Term</span>
          <InputField type="number" value={agreedFeePerTerm || ""} className="flex-1" disabled />
        </div>

        {/* Unit fees group */}
        {[
          { label: "Fee/Unit", value: "agreedFeePerUnit" },
          { label: "Discount/Unit", value: "agreedDiscountPerUnit" },
        ].map((field) => (
          <div className="flex justify-between items-center mb-3" key={field.value}>
            <span className="w-44 text-sm font-medium">{field.label}</span>
            <InputField
              type="number"
              name={field.value}
              value={values?.[field.value] ?? ""}
              className="flex-1"
              disabled
            />
          </div>
        ))}

        {/* Agreed Fee/Unit — computed */}
        <div className="flex justify-between items-center mb-3">
          <span className="w-44 text-sm font-medium">Agreed Fee/Unit</span>
          <InputField type="number" value={agreedFeePerUnit || ""} className="flex-1" disabled />
        </div>

        {/* Term schedule table — read-only, resolved fees */}
        {termSchedule.length > 0 && (
          <div className="mt-6">
            <Typography variant="subtitle2" className="font-semibold text-app-text mb-2">
              Term schedule
            </Typography>
            <TableContainer
              className="border border-app-border rounded"
              sx={{ maxHeight: 400, overflowX: "auto" }}
            >
              <Table size="small" stickyHeader sx={{ tableLayout: "auto", minWidth: 750 }}>
                <TableHead>
                  <TableRow className="bg-app-surface-muted">
                    <TableCell>#</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Census Date</TableCell>
                    <TableCell align="right">Units</TableCell>
                    <TableCell align="right">Term Fee</TableCell>
                    <TableCell align="right">Material</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Agreed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {termSchedule.map((term, index) => {
                    const fee = resolveTermFee(term);
                    const disc = resolveTermDiscount(term);
                    const agreed = computeAgreed(term);
                    return (
                      <TableRow key={term.id || index}>
                        <TableCell>T{index + 1}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDateForInput(term.date) || "—"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDateForInput(term.censusDate) || "—"}
                        </TableCell>
                        <TableCell align="right">{term.units || "—"}</TableCell>
                        <TableCell align="right">{fee || "—"}</TableCell>
                        <TableCell align="right">{term.materialFee || "—"}</TableCell>
                        <TableCell align="right">{disc || "—"}</TableCell>
                        <TableCell align="right">{agreed > 0 ? agreed : "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </div>

      <div className="p-4 border-t">
        <div className="flex justify-between gap-4">
          <Button variant="outlined" onClick={toggleDrawer} fullWidth>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button variant="contained" onClick={handleSubmit} fullWidth>
              Save
            </Button>
          )}
        </div>
      </div>
    </Drawer>
  );
}
